import { createClient, SupabaseClient } from '@supabase/supabase-js';

let supabaseClient: SupabaseClient | null = null;

/**
 * Get Supabase client with Clerk authentication
 * This version works with Clerk JWT tokens for RLS policies
 * @param clerkToken Optional Clerk session token for authenticated requests
 * @throws Error if required environment variables are missing
 */
export function getSupabaseClient(clerkToken?: string): SupabaseClient {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  // Check if env vars are missing
  if (!url || !anonKey) {
    const errorMsg = 'Missing Supabase env vars: REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY. ' +
      'Note: Environment variables must be set BEFORE building. If you added them after deployment, trigger a new build.';
    console.error('[Supabase]', errorMsg);
    throw new Error(errorMsg);
  }

  // If we have a Clerk token, create a new client with auth headers
  if (clerkToken) {
    return createClient(url, anonKey, {
      global: {
        headers: {
          Authorization: `Bearer ${clerkToken}`
        }
      },
      auth: {
        persistSession: false, // Clerk manages sessions
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
    });
  }

  // Return cached client for anonymous/public access
  if (supabaseClient) {
    return supabaseClient;
  }

  // Initialize Supabase client without Clerk auth (for public access)
  supabaseClient = createClient(url, anonKey, {
    auth: {
      persistSession: false, // Clerk manages sessions
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  });

  return supabaseClient;
}

/**
 * Helper to get authenticated Supabase client with current Clerk token
 * Use this for authenticated requests that need RLS policies
 */
export async function getAuthenticatedSupabaseClient(
  getAccessToken: () => Promise<string | undefined>
): Promise<SupabaseClient> {
  const token = await getAccessToken();
  
  if (!token) {
    console.warn('[Supabase] No Clerk token available, using unauthenticated client');
    return getSupabaseClient();
  }

  return getSupabaseClient(token);
}
